import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, CircularProgress, Alert, Dialog } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';

// Services
import { ProfileService } from '../../../services/profileService';
import { DocumentService } from '../../../services/documentService';
import { MemoryService } from '../../../services/memoryService';
import { NoteService } from '../../../services/noteService';
import { ProfileCommentService } from '../../../services/profileCommentService';
import { ResearchNoteService } from '../../../services/researchNoteService';
import { ChangeTrackingService } from '../../../services/changeTrackingService';
import { systemConfigService } from '../../../services/systemConfigService';

// Types
import type { PersonPageState, PermissionFlags } from './PersonPage.types';
import type { SectionKey } from '../../../components/profiles/subnav/types';

// Components
import { PersonHero } from './Hero/PersonHero';
import { ProfileSubnav } from '../../../components/profiles/subnav/ProfileSubnav';
import { DetailsAccordion } from './Details/DetailsAccordion';
import OwnProfileBanner from '../../../components/profiles/OwnProfileBanner';
import { ActivityFeed } from './Activity/ActivityFeed';
import { DocumentsPanel } from './Knowledge/DocumentsPanel';
import { ResearchPanel } from './Knowledge/ResearchPanel';
import { RelationshipsSnapshot } from './Essentials/RelationshipsSnapshot';
import { PhotosPreview } from './Essentials/PhotosPreview';
import { TimelineHighlight } from './Essentials/TimelineHighlight';
import { Sidebar } from './Sidebar/Sidebar';
import { PersonEditDialog } from './Edit/PersonEditDialog';
import { MetadataToggle } from './components/MetadataToggle';
import PhotoUpload from '../../../components/photos/PhotoUpload';
import DocumentUpload from '../../../components/documents/DocumentUpload';
import NoteEditor from '../../../components/notes/NoteEditor';
import MemoryEditor from '../../../components/memories/MemoryEditor';
import EventForm from '../../../components/timeline/EventForm';
import { RelationshipBuilder } from '../../../components/relationships/RelationshipBuilder';
import CommentForm from '../../../components/comments/CommentForm';
import ResearchEditor from '../../../components/research/ResearchEditor';

// Lazy loaded tab components
const PhotosTab = React.lazy(() => import('../../../components/profiles/tabs/PhotosTab'));
const PersonTimeline = React.lazy(() => import('../../../components/profiles/tabs/TimelineTab'));
const RelationshipsTab = React.lazy(() => import('../../../components/profiles/tabs/RelationshipsTab'));
const DocumentsTab = React.lazy(() => import('../../../components/profiles/tabs/DocumentsTab'));
const MemoriesTab = React.lazy(() => import('../../../components/profiles/tabs/MemoriesTab'));
const NotesTab = React.lazy(() => import('../../../components/profiles/tabs/NotesTab'));
const CommentsTab = React.lazy(() => import('../../../components/profiles/tabs/CommentsTab'));
const ResearchTab = React.lazy(() => import('../../../components/profiles/tabs/ResearchTab'));
const HistoryTab = React.lazy(() => import('../../../components/profiles/tabs/HistoryTab'));

/**
 * Type guard to validate if a string is a valid SectionKey
 */
const isValidSectionKey = (key: string | undefined): key is SectionKey => {
  const validKeys: SectionKey[] = [
    'overview',
    'relationships',
    'photos',
    'timeline',
    'history',
    'documents',
    'memories',
    'notes',
    'comments',
    'research'
  ];
  return key !== undefined && validKeys.includes(key as SectionKey);
};

/**
 * PersonPage - Complete Profile Page
 *
 * This is the main container for the profile page.
 * It handles all data fetching, state management, and layout orchestration.
 *
 * Routes:
 * - /people/:personId (default to overview tab)
 * - /people/:personId/:tab
 */
const PersonPage: React.FC = () => {
  const { personId, tab } = useParams<{ personId: string; tab?: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const currentUser = authState.user;

  // Main state
  const [state, setState] = useState<PersonPageState>({
    person: null,
    relationships: null,
    photos: [],
    timeline: [],
    counts: {
      photos: 0,        // ✅ Initialize with 0, not undefined (fixes badge issue)
      documents: 0,
      memories: 0,
      notes: 0,
      comments: 0,
      research: 0,
      timeline: 0,
      history: 0
    },
    activeTab: isValidSectionKey(tab) ? tab : 'overview',
    loading: true,
    error: null,
    sidebarCollapsed: false,
    showMetadata: false
  });

  // System configuration
  const [config, setConfig] = useState<any>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Quick Add Modal States
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [showMemoryEditor, setShowMemoryEditor] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showRelationshipBuilder, setShowRelationshipBuilder] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showResearchEditor, setShowResearchEditor] = useState(false);

  // =========================================================================
  // Permission Calculations
  // =========================================================================

  const permissions = useMemo<PermissionFlags>(() => {
    if (!currentUser) {
      return {
        canEdit: false,
        canDelete: false,
        canUpload: false,
        canApprove: false
      };
    }

    return {
      canEdit: currentUser.role === 'editor' || currentUser.role === 'admin',
      canDelete: currentUser.role === 'admin',
      canUpload: currentUser.role !== 'viewer',
      canApprove: currentUser.role === 'admin'
    };
  }, [currentUser]);

  // =========================================================================
  // Metadata Visibility Permissions (Phase 10)
  // =========================================================================

  const canSeeToggle = useMemo(() => {
    if (!config || !currentUser) return false;
    return config.metadata?.showToggleButtonTo?.includes(currentUser.role) || false;
  }, [config, currentUser]);

  const canSeeMetadata = useMemo(() => {
    if (!config || !currentUser) return false;
    if (!state.showMetadata) return false;
    return config.metadata?.showLastUpdatedTo?.includes(currentUser.role) || false;
  }, [config, currentUser, state.showMetadata]);

  // =========================================================================
  // Data Fetching
  // =========================================================================

  /**
   * Load core profile data AND all badge counts in a single operation
   * This ensures badge counts are ALWAYS available when the page renders
   * - Person info
   * - Relationships
   * - Photos
   * - Timeline events
   * - Tab counts (documents, memories, notes, comments, research, history)
   */
  const loadProfileData = useCallback(async () => {
    if (!personId) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Load profile data AND all counts in parallel
      const [profileData, docs, mems, notes, comments, research, history] = await Promise.all([
        ProfileService.getProfileData(personId),
        DocumentService.getDocumentsByPerson(personId).catch(() => []),
        MemoryService.getMemoriesByPerson(personId).catch(() => []),
        NoteService.getNotesByPerson(personId, {}).catch(() => []),
        ProfileCommentService.getCommentsByPerson(personId, false).catch(() => []),
        ResearchNoteService.getResearchNotesByPerson(personId, {}).catch(() => []),
        ChangeTrackingService.getChangeHistory('person', personId).catch(() => [])
      ]);

      setState(prev => ({
        ...prev,
        person: profileData.person,
        relationships: profileData.relationships,
        photos: profileData.photos || [],
        timeline: profileData.timeline || [],
        counts: {
          documents: docs.length,
          memories: mems.length,
          notes: notes.length,
          comments: comments.length,
          research: research.length,
          photos: 0, // Not used - calculated from state.photos.length
          timeline: 0, // Not used - calculated from state.timeline.length
          history: history.length
        },
        loading: false
      }));
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        error: err.message || 'Failed to load profile data',
        loading: false
      }));
      console.error('Error loading profile data:', err);
    }
  }, [personId]);

  /**
   * Load system configuration
   * Used for feature flags and metadata settings
   */
  const loadConfig = useCallback(async () => {
    try {
      const cfg = await systemConfigService.getConfig();
      setConfig(cfg);

      // Set initial metadata visibility based on config
      if (cfg.metadata?.showLastUpdated) {
        setState(prev => ({ ...prev, showMetadata: true }));
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
  }, []);

  // =========================================================================
  // Effects
  // =========================================================================

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Load profile data when personId changes
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Update active tab when URL changes
  useEffect(() => {
    if (isValidSectionKey(tab)) {
      setState(prev => ({ ...prev, activeTab: tab }));
    }
  }, [tab]);

  // =========================================================================
  // Event Handlers
  // =========================================================================

  const handleTabChange = (newTab: string) => {
    navigate(`/people/${personId}/${newTab}`);
  };

  const handleEdit = () => {
    if (!permissions.canEdit) {
      alert('You do not have permission to edit profiles');
      return;
    }
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditSave = async () => {
    // Refresh profile data after edit
    await loadProfileData();
  };

  const handleMetadataToggle = () => {
    setState(prev => ({ ...prev, showMetadata: !prev.showMetadata }));
  };

  // Timeline event handlers (for tab component compatibility)
  const handleCreateEvent = async (eventData: any) => {
    // Event creation handled by TimelineTab - just refresh data after
    await loadProfileData();
  };

  const handleUpdateEvent = async (eventId: string, updates: any) => {
    // Event update handled by TimelineTab - just refresh data after
    await loadProfileData();
  };

  const handleDeleteEvent = async (eventId: string) => {
    // Event deletion handled by TimelineTab - just refresh data after
    await loadProfileData();
  };

  const handleLinkEventToPhoto = async (eventId: string, photoId: string) => {
    // Link handled by TimelineTab - just refresh data after
    await loadProfileData();
  };

  const handleLinkEventToRelationship = async (eventId: string, relationshipId: string) => {
    // Link handled by TimelineTab - just refresh data after
    await loadProfileData();
  };

  // =========================================================================
  // Quick Add Modal Handlers
  // =========================================================================

  const modalHandlers = useMemo(() => ({
    openPhotoUpload: () => setShowPhotoUpload(true),
    openDocumentUpload: () => setShowDocumentUpload(true),
    openNoteEditor: () => setShowNoteEditor(true),
    openMemoryEditor: () => setShowMemoryEditor(true),
    openEventForm: () => setShowEventForm(true),
    openRelationshipBuilder: () => setShowRelationshipBuilder(true),
    openCommentForm: () => setShowCommentForm(true),
    openResearchEditor: () => setShowResearchEditor(true)
  }), []);

  // =========================================================================
  // Tab Content Rendering
  // =========================================================================

  const LoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <CircularProgress size={32} />
    </Box>
  );

  const renderTabContent = () => {
    if (!state.person) return null;

    switch (state.activeTab) {
      case 'overview':
        // Overview tab with comprehensive panels + Sidebar
        return (
          <Grid container spacing={3}>
            {/* Main Column */}
            <Grid item xs={12} lg={9}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Basic Information Accordion */}
                <DetailsAccordion
                  person={state.person}
                  showMetadata={canSeeMetadata}
                  metadataFormat={config?.metadata?.displayFormat || 'short'}
                />

                {/* Timeline + Family Row */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TimelineHighlight
                      events={state.timeline.slice(0, 3)}
                      totalCount={state.counts.timeline}
                      personId={state.person.id}
                      showMetadata={canSeeMetadata}
                      metadataFormat={config?.metadata?.displayFormat || 'short'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RelationshipsSnapshot
                      relationships={state.relationships}
                      personId={state.person.id}
                      showMetadata={canSeeMetadata}
                      metadataFormat={config?.metadata?.displayFormat || 'short'}
                    />
                  </Grid>
                </Grid>

                {/* Documents + Research Row */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DocumentsPanel
                      personId={state.person.id}
                      person={state.person}
                      showMetadata={canSeeMetadata}
                      metadataFormat={config?.metadata?.displayFormat || 'short'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ResearchPanel
                      personId={state.person.id}
                      person={state.person}
                      showMetadata={canSeeMetadata}
                      metadataFormat={config?.metadata?.displayFormat || 'short'}
                    />
                  </Grid>
                </Grid>

                {/* Photos Preview (Full Width) */}
                <PhotosPreview
                  photos={state.photos.slice(0, 6)}
                  totalCount={state.counts.photos}
                  personId={state.person.id}
                  showMetadata={canSeeMetadata}
                  metadataFormat={config?.metadata?.displayFormat || 'short'}
                />

                {/* Activity & Collaboration (Full Width) */}
                <ActivityFeed
                  personId={state.person.id}
                  personName={`${state.person.firstName} ${state.person.lastName}`}
                  showMetadata={canSeeMetadata}
                  metadataFormat={config?.metadata?.displayFormat || 'short'}
                />
              </Box>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'photos':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <PhotosTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'timeline':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <PersonTimeline
                person={state.person}
                timelineEvents={state.timeline}
                photos={state.photos}
                onCreateEvent={handleCreateEvent}
                onUpdateEvent={handleUpdateEvent}
                onDeleteEvent={handleDeleteEvent}
                onLinkEventToPhoto={handleLinkEventToPhoto}
                onLinkEventToRelationship={handleLinkEventToRelationship}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'relationships':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <RelationshipsTab person={state.person} relationships={state.relationships || { personId: state.person.id, spouses: [], children: [], parents: [], siblings: [] }} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'documents':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <DocumentsTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'memories':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <MemoriesTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'notes':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <NotesTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'comments':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <CommentsTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'research':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <ResearchTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      case 'history':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <HistoryTab person={state.person} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Sidebar
                person={state.person}
                counts={state.counts}
                permissions={permissions}
                modalHandlers={modalHandlers}
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // =========================================================================
  // Render States
  // =========================================================================

  // Loading state
  if (state.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        sx={{ bgcolor: 'background.default' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (state.error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            {state.error}
          </Alert>
        </Container>
      </Box>
    );
  }

  // No person found
  if (!state.person) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="md">
          <Alert severity="warning">
            Person not found. They may have been deleted or you may not have permission to view this profile.
          </Alert>
        </Container>
      </Box>
    );
  }

  // =========================================================================
  // Main Render
  // =========================================================================

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Own profile banner */}
      <Container maxWidth="xl" sx={{ pt: 2 }}>
        <OwnProfileBanner personId={state.person.id} />
      </Container>

      {/* Hero Section */}
      <PersonHero
        person={state.person}
        completeness={state.person.completeness || 0}
        onEdit={handleEdit}
        showMetadata={canSeeMetadata}
        metadataFormat={config?.metadata?.displayFormat || 'short'}
      />

      {/* Sub-navigation */}
      <Box sx={{ position: 'sticky', top: 64, zIndex: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="xl">
          <ProfileSubnav
            personId={personId!}
            activeKey={state.activeTab}
            counts={{
              overview: undefined,
              relationships: state.relationships ?
                state.relationships.spouses.length +
                state.relationships.children.length +
                state.relationships.parents.length +
                state.relationships.siblings.length : 0,
              photos: state.photos.length,
              timeline: state.timeline.length,
              history: state.counts.history,
              documents: state.counts.documents,
              memories: state.counts.memories,
              notes: state.counts.notes,
              comments: state.counts.comments,
              research: state.counts.research
            }}
            onNavigate={handleTabChange}
            sticky={false}
          />
        </Container>
      </Box>

      {/* Main Content - Tab-Specific */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Suspense fallback={<LoadingFallback />}>
          {renderTabContent()}
        </Suspense>
      </Container>

      {/* Edit Dialog */}
      {state.person && (
        <PersonEditDialog
          open={editDialogOpen}
          person={state.person}
          onClose={handleEditClose}
          onSave={handleEditSave}
          permissions={permissions}
        />
      )}

      {/* Quick Add Modals */}
      {state.person && currentUser && (
        <>
          {/* Photo Upload Dialog */}
          <Dialog
            open={showPhotoUpload}
            onClose={() => setShowPhotoUpload(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { minHeight: '600px' } }}
          >
            <PhotoUpload
              personId={state.person.id}
              onUploadComplete={async () => {
                setShowPhotoUpload(false);
                await loadProfileData();
              }}
              onCancel={() => setShowPhotoUpload(false)}
            />
          </Dialog>

          {/* Document Upload Dialog */}
          <Dialog
            open={showDocumentUpload}
            onClose={() => setShowDocumentUpload(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { minHeight: '600px' } }}
          >
            <DocumentUpload
              personId={state.person.id}
              personName={`${state.person.firstName} ${state.person.lastName}`}
              currentUser={currentUser}
              onUploadComplete={async () => {
                setShowDocumentUpload(false);
                await loadProfileData();
              }}
              onCancel={() => setShowDocumentUpload(false)}
            />
          </Dialog>

          {/* Note Editor Dialog */}
          <Dialog
            open={showNoteEditor}
            onClose={() => setShowNoteEditor(false)}
            maxWidth="md"
            fullWidth
          >
            <NoteEditor
              personId={state.person.id}
              personName={`${state.person.firstName} ${state.person.lastName}`}
              currentUser={currentUser}
              existingNote={null}
              onSave={async () => {
                setShowNoteEditor(false);
                await loadProfileData();
              }}
              onCancel={() => setShowNoteEditor(false)}
            />
          </Dialog>

          {/* Memory Editor Dialog */}
          <Dialog
            open={showMemoryEditor}
            onClose={() => setShowMemoryEditor(false)}
            maxWidth="md"
            fullWidth
          >
            <MemoryEditor
              personId={state.person.id}
              personName={`${state.person.firstName} ${state.person.lastName}`}
              currentUser={currentUser}
              existingMemory={null}
              onSave={async () => {
                setShowMemoryEditor(false);
                await loadProfileData();
              }}
              onCancel={() => setShowMemoryEditor(false)}
            />
          </Dialog>

          {/* Event Form (has its own dialog) */}
          <EventForm
            open={showEventForm}
            personId={state.person.id}
            onClose={() => setShowEventForm(false)}
            onSubmit={async () => {
              setShowEventForm(false);
              await loadProfileData();
            }}
          />

          {/* Relationship Builder (has its own dialog) */}
          <RelationshipBuilder
            personId={state.person.id}
            open={showRelationshipBuilder}
            onRelationshipCreated={async () => {
              setShowRelationshipBuilder(false);
              await loadProfileData();
            }}
            onCancel={() => setShowRelationshipBuilder(false)}
          />

          {/* Comment Form Dialog */}
          <Dialog
            open={showCommentForm}
            onClose={() => setShowCommentForm(false)}
            maxWidth="md"
            fullWidth
          >
            <Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                <Box sx={{ typography: 'h6' }}>Add Comment</Box>
                <Box
                  component="button"
                  onClick={() => setShowCommentForm(false)}
                  sx={{
                    border: 'none',
                    background: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary' }
                  }}
                >
                  ×
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <CommentForm
                  personId={state.person.id}
                  currentUser={currentUser}
                  onSubmit={async () => {
                    setShowCommentForm(false);
                    await loadProfileData();
                  }}
                  onCancel={() => setShowCommentForm(false)}
                />
              </Box>
            </Box>
          </Dialog>

          {/* Research Editor Dialog */}
          <Dialog
            open={showResearchEditor}
            onClose={() => setShowResearchEditor(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { minHeight: '70vh' } }}
          >
            <ResearchEditor
              personId={state.person.id}
              personName={`${state.person.firstName} ${state.person.lastName}`}
              currentUser={currentUser}
              existingNote={null}
              onSave={async () => {
                setShowResearchEditor(false);
                await loadProfileData();
              }}
              onCancel={() => setShowResearchEditor(false)}
            />
          </Dialog>
        </>
      )}

      {/* Metadata Toggle FAB (Phase 10) */}
      {canSeeToggle && (
        <MetadataToggle
          show={state.showMetadata}
          onChange={handleMetadataToggle}
        />
      )}
    </Box>
  );
};

export default PersonPage;
